import { useState } from 'react';
import type { InventoryItem, Warehouse } from '../types/inventory.types';
import { InventoryService } from '../services';

interface MovementData {
  movementDirection?: 'IN' | 'OUT';
  referenceNumber?: string;
  description?: string;
  date?: Date;
  unitPrice?: number;
  warehouseStockId?: string;
  warehouseId?: string;
  locationId?: string;
}

const normalizeId = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

interface UseInventoryValueUpdateProps {
  companyId: string | null;
  warehouses: Warehouse[];
  n11IntegrationActive: boolean;
  showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  setInventoryItems: React.Dispatch<React.SetStateAction<InventoryItem[]>>;
  onCloseModal: () => void;
}

interface UpdateValueParams {
  selectedItem: InventoryItem | null;
  selectedField: 'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name' | null;
  updateValue: number;
  updateListPrice: number;
  updateStringValue: string;
  syncWithN11: boolean;
  userDetails: any;
  movementData?: MovementData;
}

export const useInventoryValueUpdate = ({
  companyId,
  warehouses,
  n11IntegrationActive,
  showSnackbar,
  setInventoryItems,
  onCloseModal,
}: UseInventoryValueUpdateProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isN11Syncing, setIsN11Syncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const syncProductWithN11 = async (
    item: InventoryItem,
    field: 'quantity' | 'salePrice',
    value: number
  ) => {
    // N11 senkronizasyon mantığı burada olacak
    console.log('N11 senkronizasyonu:', item, field, value);
    // Bu fonksiyon daha sonra genişletilebilir
  };

  const handleUpdateValue = async (params: UpdateValueParams) => {
    const {
      selectedItem,
      selectedField,
      updateValue,
      updateListPrice,
      updateStringValue,
      syncWithN11,
      userDetails,
      movementData,
    } = params;

    if (!selectedItem || !selectedField || !companyId) return;
    
    console.log('🚀 [UPDATE VALUE] Güncelleme başlatıldı');
    console.log('📋 [UPDATE VALUE] selectedItem:', selectedItem);
    console.log('📋 [UPDATE VALUE] selectedField:', selectedField);
    console.log('📋 [UPDATE VALUE] updateValue:', updateValue);
    console.log('📋 [UPDATE VALUE] movementData:', movementData);
    console.log('📋 [UPDATE VALUE] selectedItem.isQRMenu:', (selectedItem as any).isQRMenu);
    
    setIsUpdating(true);
    try {
      // ✅ SQL kullanarak güncelle
      if (selectedField === 'quantity') {
        const fallbackSingleStock =
          selectedItem.warehouseStocks && selectedItem.warehouseStocks.length === 1
            ? selectedItem.warehouseStocks[0]
            : undefined;

        // Detay satırından açılan modalda bağlamın (depo/lokasyon) kaybolmaması için
        // movementData öncelikli, yoksa selectedItem içindeki tek satırdan fallback al.
        const selectedWarehouseId = normalizeId(
          movementData?.warehouseId ||
          fallbackSingleStock?.warehouseId
        );
        const selectedLocationId = normalizeId(
          movementData?.locationId ??
          fallbackSingleStock?.locationId
        );
        const selectedWarehouseStockId = normalizeId(
          movementData?.warehouseStockId ||
          fallbackSingleStock?.id
        );

        // Depo satırından açıldıysa ilgili depoyu, aksi halde merkez depoyu kullan.
        const targetWarehouse = selectedWarehouseId
          ? warehouses.find(w => w.id === selectedWarehouseId)
          : warehouses.find(w =>
              w.name.toLowerCase().includes('merkez') ||
              w.name.toLowerCase() === 'merkez depo' ||
              w.name.toLowerCase() === 'ana depo'
            );

        if (!targetWarehouse) {
          showSnackbar('Hedef depo bulunamadı. Lütfen stok hareketini tekrar açın.', 'error');
          onCloseModal();
          return;
        }

        const isSmartWarehouse = (targetWarehouse.warehouseType || '').toLowerCase() === 'smart';
        // Smart depoda lokasyonsuz satırlar sadece temizlik (OUT) için kullanılabilir.
        if (isSmartWarehouse && !selectedLocationId && movementData?.movementDirection === 'IN') {
          showSnackbar(
            'Bu satır lokasyonsuz/bozuk kayıt. Stok Giriş yapılamaz; sadece Stok Çıkış ile temizlenebilir.',
            'warning'
          );
          setIsUpdating(false);
          return;
        }

        // ✅ YENİ SİSTEM: Manuel stok hareketi oluştur (ZORUNLU)
        if (!movementData || !movementData.movementDirection) {
          showSnackbar('Lütfen Stok Giriş veya Stok Çıkış seçimi yapın!', 'error');
          setIsUpdating(false);
          return;
        }
        
        console.log(`📦 [MANUAL STOCK MOVEMENT] Manuel stok hareketi oluşturuluyor...`);
        
        const result = await InventoryService.createManualStockMovement({
          companyId: companyId,
          productId: selectedItem.id,
          warehouseStockId: selectedWarehouseStockId,
          warehouseId: targetWarehouse.id,
          direction: movementData.movementDirection,
          quantity: updateValue,
          unitPrice: movementData.unitPrice || 0,
          referenceNumber: movementData.referenceNumber,
          description: movementData.description,
          date: movementData.date?.toISOString() || new Date().toISOString(),
          createdBy: userDetails?.email || 'system',
          locationId: selectedLocationId,
        });
        
        console.log(`✅ [MANUAL STOCK MOVEMENT] Stok hareketi kaydedildi:`, result);
        
        // State'i yeni miktar ile güncelle
        setInventoryItems(prevItems =>
          prevItems.map(item => {
            if (item.id === selectedItem.id) {
              const currentStocks = item.warehouseStocks || [];

              // Önce stockId ile tek satırı hedefle; yoksa depo+lokasyon fallback.
              const targetIndexById = selectedWarehouseStockId
                ? currentStocks.findIndex(ws => ws.id === selectedWarehouseStockId)
                : -1;
              const targetIndexByWarehouseLocation = currentStocks.findIndex(ws =>
                ws.warehouseId === targetWarehouse.id &&
                ((selectedLocationId && ws.locationId === selectedLocationId) ||
                 (!selectedLocationId && !ws.locationId))
              );
              const targetIndex = targetIndexById >= 0 ? targetIndexById : targetIndexByWarehouseLocation;

              let updatedWarehouseStocks = [...currentStocks];
              if (targetIndex >= 0) {
                if (result.newQuantity === 0) {
                  // Backend'de satır fiziksel siliniyor; UI'da da hemen kaldıralım.
                  updatedWarehouseStocks.splice(targetIndex, 1);
                } else {
                  updatedWarehouseStocks[targetIndex] = {
                    ...updatedWarehouseStocks[targetIndex],
                    quantity: result.newQuantity,
                    updatedAt: new Date().toISOString()
                  };
                }
              } else if (result.newQuantity > 0) {
                // Sadece gerçekten bulunamıyorsa ve pozitifse yeni satır ekle.
                updatedWarehouseStocks.push({
                  id: selectedWarehouseStockId || `tmp-${targetWarehouse.id}-${selectedLocationId || 'noloc'}`,
                  productId: item.id,
                  warehouseId: targetWarehouse.id,
                  warehouseName: targetWarehouse.name,
                  quantity: result.newQuantity,
                  locationId: selectedLocationId || undefined,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                });
              }

              return { ...item, warehouseStocks: updatedWarehouseStocks };
            }
            return item;
          })
        );
        
        showSnackbar(`Stok hareketi başarıyla kaydedildi. Yeni miktar: ${result.newQuantity}`, 'success');
      } else if (selectedField === 'salePrice') {
        // Fiyat güncelleme
        console.log('💰 [UPDATE VALUE] SQL\'de fiyat güncelleniyor...');
        console.log('👤 [UPDATE VALUE] userDetails:', userDetails);
        console.log('👤 [UPDATE VALUE] userDetails?.id:', userDetails?.id);
        console.log('👤 [UPDATE VALUE] userDetails?.firstName:', userDetails?.firstName);
        console.log('👤 [UPDATE VALUE] userDetails?.lastName:', userDetails?.lastName);
        
        // ✅ Kullanıcı bilgilerini hazırla (log için)
        const userId = userDetails?.id || undefined;
        const userName = userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || undefined : undefined;
        
        console.log('👤 [UPDATE VALUE] Hesaplanan userId:', userId);
        console.log('👤 [UPDATE VALUE] Hesaplanan userName:', userName);
        
        await InventoryService.updatePrice(
          companyId, 
          selectedItem.id, 
          updateValue, 
          updateListPrice > 0 ? updateListPrice : undefined,
          userId,
          userName
        );
        
        // State'i güncelle
        setInventoryItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id 
              ? { 
                  ...item, 
                  salePrice: updateValue,
                  ...(updateListPrice > 0 && { listPrice: updateListPrice })
                } 
              : item
          )
        );
      } else if (selectedField === 'vatRate') {
        // KDV oranı güncelleme
        console.log('📊 [UPDATE VALUE] SQL\'de KDV oranı güncelleniyor...');
        await InventoryService.updateItem(companyId, selectedItem.id, {
          id: selectedItem.id,
          companyId: companyId,
          stockCode: selectedItem.stockCode,
          name: selectedItem.name,
          salePrice: selectedItem.salePrice,
          saleCurrency: selectedItem.saleCurrency || 'TRY',
          unit: selectedItem.unit,
          vatRate: updateValue,
          vatStatus: selectedItem.vatStatus || 'excluded',
          isActive: selectedItem.isActive,
          canBeNegative: selectedItem.canBeNegative || false,
          departmentNo: selectedItem.departmentNo ?? undefined,
          // ✅ KRITIK: Resim URL'lerini korumak için mevcut değerleri gönder
          imageUrl: (selectedItem as any).imageUrl || null,
          imageUrls: (selectedItem as any).imageUrls || null,
        });
        
        // State'i güncelle
        setInventoryItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id 
              ? { ...item, vatRate: updateValue } 
              : item
          )
        );
        
        console.log('✅ [UPDATE VALUE] KDV oranı güncellendi:', updateValue);
      } else if (selectedField === 'departmentNo') {
        // ÖKC (Departman No) güncelleme
        console.log('🔢 [UPDATE VALUE] SQL\'de ÖKC (Departman No) güncelleniyor...');
        await InventoryService.updateItem(companyId, selectedItem.id, {
          id: selectedItem.id,
          companyId: companyId,
          stockCode: selectedItem.stockCode,
          name: selectedItem.name,
          salePrice: selectedItem.salePrice,
          saleCurrency: selectedItem.saleCurrency || 'TRY',
          unit: selectedItem.unit,
          vatRate: selectedItem.vatRate || 0,
          vatStatus: selectedItem.vatStatus || 'excluded',
          isActive: selectedItem.isActive,
          canBeNegative: selectedItem.canBeNegative || false,
          departmentNo: updateValue,
          // ✅ KRITIK: Resim URL'lerini korumak için mevcut değerleri gönder
          imageUrl: (selectedItem as any).imageUrl || null,
          imageUrls: (selectedItem as any).imageUrls || null,
        });
        
        // State'i güncelle
        setInventoryItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id 
              ? { ...item, departmentNo: updateValue } 
              : item
          )
        );
        
        console.log('✅ [UPDATE VALUE] ÖKC (Departman No) güncellendi:', updateValue);
      } else if (selectedField === 'name') {
        // Ürün adı güncelleme
        if (!updateStringValue.trim()) {
          showSnackbar('Ürün adı boş olamaz', 'error');
          setIsUpdating(false);
          return;
        }
        
        console.log('📝 [UPDATE VALUE] SQL\'de ürün adı güncelleniyor...');
        await InventoryService.updateItem(companyId, selectedItem.id, {
          id: selectedItem.id,
          companyId: companyId,
          stockCode: selectedItem.stockCode,
          name: updateStringValue.trim(),
          salePrice: selectedItem.salePrice,
          saleCurrency: selectedItem.saleCurrency || 'TRY',
          unit: selectedItem.unit,
          vatRate: selectedItem.vatRate || 0,
          vatStatus: selectedItem.vatStatus || 'excluded',
          isActive: selectedItem.isActive,
          canBeNegative: selectedItem.canBeNegative || false,
          departmentNo: selectedItem.departmentNo ?? undefined,
          // ✅ KRITIK: Resim URL'lerini korumak için mevcut değerleri gönder
          imageUrl: (selectedItem as any).imageUrl || null,
          imageUrls: (selectedItem as any).imageUrls || null,
        });
        
        // State'i güncelle
        setInventoryItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id 
              ? { ...item, name: updateStringValue.trim() } 
              : item
          )
        );
        
        console.log('✅ [UPDATE VALUE] Ürün adı güncellendi:', updateStringValue.trim());
      }
      
      console.log('✅ [UPDATE VALUE] SQL güncelleme başarılı');
      
      // N11 ve QR Menü senkronizasyonu sadece quantity ve salePrice için
      if (selectedField === 'quantity' || selectedField === 'salePrice') {
        // N11 senkronizasyonu
        if (syncWithN11 && n11IntegrationActive) {
          setIsN11Syncing(true);
          try {
            await syncProductWithN11(selectedItem, selectedField, updateValue);
          } catch (n11Error) {
            console.error('N11 senkronizasyon hatası:', n11Error);
            showSnackbar('Ürün güncellendi ancak N11 senkronizasyonunda hata oluştu: ' + (n11Error instanceof Error ? n11Error.message : 'Bilinmeyen hata'), 'warning');
          } finally {
            setIsN11Syncing(false);
          }
        }
        
        // QR Menü için ayrı bir senkronizasyon adımı YOK: menü, ürünleri
        // InventoryItems'tan canlı okuduğu icin fiyat/miktar değişikliği
        // kaydedildiği anda menüye de yansır.
        if ((selectedItem as any).isQRMenu === true) {
          showSnackbar(
            selectedField === 'salePrice'
              ? 'Satış fiyatı güncellendi, QR Menü fiyatı da güncellendi'
              : 'Miktar güncellendi, QR Menü de güncellendi',
            'success'
          );
        }
      }
      
      // KDV, ÖKC ve Ürün Adı güncellemelerinde basit başarı mesajı
      if (selectedField === 'vatRate') {
        showSnackbar(`KDV oranı %${updateValue} olarak güncellendi`, 'success');
      } else if (selectedField === 'departmentNo') {
        showSnackbar(`ÖKC (Departman No) ${updateValue} olarak güncellendi`, 'success');
      } else if (selectedField === 'name') {
        showSnackbar(`Ürün adı "${updateStringValue.trim()}" olarak güncellendi`, 'success');
      }
      
      setError(null);
      console.log('Değer başarıyla güncellendi');
      onCloseModal();
    } catch (error: any) {
      console.error("Güncelleme sırasında hata oluştu:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Güncelleme sırasında bir hata oluştu';
      setError(apiMessage);
      showSnackbar(apiMessage, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    handleUpdateValue,
    isUpdating,
    isN11Syncing,
    error,
    setError,
  };
};

